import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Mail, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("verifying"); // verifying | ok | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("No verification token in the URL.");
      return;
    }
    api
      .get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then((r) => {
        setState("ok");
        setMessage(`Email ${r.data.email} verified. You can close this tab.`);
      })
      .catch((e) => {
        setState("error");
        setMessage(
          e?.response?.data?.detail ||
            "Could not verify this token. It may have expired."
        );
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center space-y-6">
        <Link to="/" className="inline-flex items-center gap-2">
          <Activity className="h-6 w-6 text-[hsl(var(--accent))]" />
          <span className="text-xl font-bold tracking-tight">Elswitch</span>
        </Link>
        <div className="rounded-lg border border-border bg-card p-6 space-y-3">
          {state === "verifying" && (
            <>
              <Mail className="h-10 w-10 mx-auto text-[hsl(var(--accent))] animate-pulse" />
              <p className="text-sm text-muted-foreground">Verifying your email…</p>
            </>
          )}
          {state === "ok" && (
            <>
              <CheckCircle2
                className="h-10 w-10 mx-auto text-[hsl(var(--ok))]"
                data-testid="verify-ok-icon"
              />
              <h2 className="text-lg font-semibold">Email verified</h2>
              <p className="text-sm text-muted-foreground">{message}</p>
              <Link to="/login">
                <Button className="mt-2">Continue to sign in</Button>
              </Link>
            </>
          )}
          {state === "error" && (
            <>
              <XCircle
                className="h-10 w-10 mx-auto text-[hsl(var(--danger))]"
                data-testid="verify-err-icon"
              />
              <h2 className="text-lg font-semibold">Verification failed</h2>
              <p className="text-sm text-muted-foreground">{message}</p>
              <p className="text-[11px] text-muted-foreground">
                You can request a fresh link from Settings → Account once
                you're signed in.
              </p>
              <Link to="/login">
                <Button variant="outline" className="mt-2">
                  Back to sign in
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
